import QueryTags from '../../types/QueryTags'
import BanksResponse from './types/response/banks'
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions'
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react'
import { AccountLimitsResponse, CurrenciesResponse, DeleteScheduledResponse, EKashAccountResponse, ExchangeRateResponse, FetchBankIconResponse, InitiateEkashTransferResponse, InitiateTransferResponse, RegisterEkashResponse, ScheduledTransferResponse, ScheduleTransferResponse, TransferHistoryResponse, TransferResponse, ValidateAccountResponse, ValidateEkashResponse, ValidateLimitResponse, ValidateAlipayResponse, InitiateAlipayTransferResponse, InitiateSwiftResponse, ValidateSwiftResponse, ExchangeRateResponseV2, GetCountriesOnafriqResponse, GetPartnersOnafriqResponse, GetBanksOnafriqResponse, ValidateAccountOnafriqResponse, GetOnafriqRateResponse, InitiatePappsTransferResponse, FetchPappsBanksResponse, FetchPappsRateResponse, StandingOrderResponse, GetAuthorizationPendingApproval, GetAuthorizationPendingApprovalList, TransactionHistoryResponse, FXRatesResponse, GetNegotiatedRates, NegotiatedRateResponse, ValidateBalanceResponse } from './types/response'
import { AccountLimitsRequest, DeleteScheduledRequest, ExchangeRateRequest, InitiateEkashTransferRequest, InitiateTransferRequest, RegisterEkashRequest, ScheduleTransferRequest, TransferRequest, ValidateAccountRequest, ValidateEkashRequest, ValidateLimitRequest, ValidateAlipayRequest, InitiateAlipayTransferRequest, InitiateSwiftRequest, InitiateTransferOnafriqRequest, GetOnafriqRateRequest, InitiatePappsTransferRequest, FetchPappsRatesRequest, GetStandingOrdersRequest, DeleteStandingOrderRequest } from './types/request'
import InitiateTransferOnafriqResponse from './types/response/initiateTransferOnafriq'
import ValidateAccountOnafriqRequest from './types/request/validateAccountOnafriq'
import { getBalanceValidationParams } from 'utils/balanceValidation'

const transferPath = '/transfer-service'
const inquiryPath = '/inquiry-services/api'
const inquiryPathV2 = '/inquiry-services/api/v2'

const transferEndPoint = (builder: EndpointBuilder<
  BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>, QueryTags, 'data'>) => 
  ({
    getBanks: builder.query<BanksResponse, string>({
      query: () => {
        return(
          {
            url: `${transferPath}/banks`,
            method: 'GET',
          }
        )
      }
    }),
    validateLimit: builder.query<ValidateLimitResponse, ValidateLimitRequest>({
      query: (args) => {
        return ({
          url:`${transferPath}/validate-transaction-limit`,
          method: 'GET',
          params: args,
        })
      }
    }),
    validateBalance: builder.query<ValidateBalanceResponse, { accountNumber: string, amount: string, transactionCurrency: string, debitCurrency: string, transactionType: string, creditAccount: string, creditCurrency: string, hasBkAdvance?: boolean, }>({
      query: (args) => {
        return ({
          url:`${transferPath}/validate-account-balance`,
          method: 'GET',
          params: { ...args, ...getBalanceValidationParams() },
        })
      }
    }),
    initiateTransfer: builder.mutation<InitiateTransferResponse, InitiateTransferRequest>({
      query: ({ otpCode, emailOrPhoneNumber, otpIdentifier, ...body}) => {
        return ({
          url:`${transferPath}/transfer`,
          method: 'POST',
          body,
          params: { otpCode, emailOrPhoneNumber, otpIdentifier},
        })
      },
      invalidatesTags: ['transferHistory']
    }),
    validateAccount: builder.query<ValidateAccountResponse, ValidateAccountRequest>({
      query: (args) => {
        return ({
          url:`${transferPath}/validate`,
          method: 'GET',
          params: args,
        })
      }
    }),
    getAccountLimit: builder.query<AccountLimitsResponse, AccountLimitsRequest>({
      query: (args) => {
        return ({
          url:`${transferPath}/transaction-limit-details`,
          method: 'GET',
          params: args,
        })
      }
    }),
    getCurrencies: builder.query<CurrenciesResponse, string>({
      query: () => {
        return ({
          url:`${inquiryPath}/currency`,
          method: 'GET',
        })
      }
    }),
    getExchangeRate: builder.query<ExchangeRateResponse, ExchangeRateRequest>({
      query: (args) => {
        return ({
          url:`${inquiryPath}/currency-exchange-rate`,
          method: 'GET',
          params: args,
        })
      }
    }),
    getExchangeRateV2: builder.query<ExchangeRateResponseV2, ExchangeRateRequest>({
      query: (args) => {
        return ({
          url:`${inquiryPathV2}/currency-exchange-rate`,
          method: 'GET',
          params: args,
        })
      }
    }),
    getTransfersHistory: builder.query<TransferHistoryResponse, TransferRequest>({
      query: ({ customerId, ...params }) => {
        return ({
          url:`${transferPath}/view-transfer/${customerId}`,
          method: 'GET',
          params: params,
        })
      },
      providesTags: ['transferHistory']
    }),
    scheduleTransfer: builder.mutation<ScheduleTransferResponse, ScheduleTransferRequest>({
      query: ({ otpCode, emailOrPhoneNumber, otpIdentifier, ...body}) => {
        return ({
          url:`${transferPath}/schedule`,
          method: 'POST',
          body,
          params: { otpCode, emailOrPhoneNumber, otpIdentifier },
        })
      },
      invalidatesTags: ['scheduleTransferHistory']
    }),
    getScheduledTransfers: builder.query<ScheduledTransferResponse, TransferRequest>({
      query: (args) => {
        return ({
          url:`${transferPath}/schedule`,
          method: 'GET',
          params: args,
        })
      },
      providesTags: ['scheduleTransferHistory']
    }),
    deleteScheduledTransfer: builder.mutation<DeleteScheduledResponse, DeleteScheduledRequest>({
      query: ({ id }) => {
        return ({
          url:`${transferPath}/scheduled-transfer/${id}/scheduled-transfer-id`,
          method: 'DELETE',
        })
      },
      invalidatesTags: ['scheduleTransferHistory']
    }),
    validateEkashAccount: builder.query<ValidateEkashResponse, ValidateEkashRequest>({
      query: (args) => {
        return ({
          url:`${transferPath}/validate-rndps-account`,
          method: 'GET',
          params: args,
        })
      }
    }),
    initiateEkashTransfer: builder.mutation<InitiateEkashTransferResponse, InitiateEkashTransferRequest>({
      query: ({ otpCode, emailOrPhoneNumber,otpIdentifier, ...body}) => {
        return ({
          url:`${transferPath}/rndps-transfer-initiation`,
          method: 'POST',
          body,
          params: { otpCode, emailOrPhoneNumber, otpIdentifier },
        })
      },
      invalidatesTags: ['transferHistory']
    }),
    getEkashAccounts: builder.query<EKashAccountResponse[], string>({
      query: () =>  `${transferPath}/rndps-accounts`
    }),
    registerEkashTransfer: builder.mutation<RegisterEkashResponse, RegisterEkashRequest>({
      query: (body) => {
        return ({
          url:`${transferPath}/rndps-account-opening`,
          method: 'POST',
          body,
        })
      }
    }),
    validateAlipayTransfer: builder.mutation<ValidateAlipayResponse, ValidateAlipayRequest>({
      query: (body) => {
        return ({
          url:`${transferPath}/validate-alipay`,
          method: 'POST',
          body,
        })
      }
    }),
    initiateAlipayTransfer: builder.mutation<InitiateAlipayTransferResponse, InitiateAlipayTransferRequest>({
      query: ({ otpCode, emailOrPhoneNumber, ...body}) => {
        return ({
          url:`${transferPath}/initiate-alipay`,
          method: 'POST',
          body,
          params: { otpCode, emailOrPhoneNumber },
        })
      },
      invalidatesTags: ['transferHistory']
    }),
    initiateSwiftTransfer: builder.mutation<InitiateSwiftResponse, InitiateSwiftRequest>({
      query: (body) => {
        return ({
          url:`${transferPath}/initiate-swift`,
          method: 'POST',
          body,
          // params: { otpCode, emailOrPhoneNumber, otpIdentifier},
        })
      },
      invalidatesTags: ['transferHistory']
    }),
    getPaymentPurposes: builder.query<{ code: string, purpose: string, }[], string>({
      query: () =>  `${inquiryPath}/swift/payment-purposes`
    }),
    validateSwiftCode: builder.mutation<ValidateSwiftResponse, { swiftCode: string, }>({
      query: (params) => {
        return ({
          url:`${inquiryPath}/swift/validate-swift-code`,
          method: 'POST',
          params,
        })
      }
    }),
    getCountriesList: builder.query<GetCountriesOnafriqResponse, string>({
      query: (query) => `${transferPath}/${query}`
    }),
    getPartnersOnafriq: builder.query<GetPartnersOnafriqResponse, void>({
      query: () => `${transferPath}/onafriq/getPartners`
    }),
    getBanksOnafriq: builder.query<GetBanksOnafriqResponse, { countryCode: string, }>({
      query: (params) => {
        return ({
          url: `${transferPath}/onafriq/getBanks`,
          params,
          method: 'GET',
        })
      },
    }),
    initiateTransferOnafriq: builder.mutation<InitiateTransferOnafriqResponse, InitiateTransferOnafriqRequest>({
      query: ({ otpCode, otpIdentifier, ...body }) => {
        return ({
          url:`${transferPath}/onafriq/initiate-transfer`,
          method: 'POST',
          params: { otpCode, otpIdentifier },
          body,
        })
      },
      invalidatesTags: ['transferHistory']
    }),
    validateAccountOnafriq: builder.mutation<ValidateAccountOnafriqResponse, ValidateAccountOnafriqRequest>({
      query: (body) => {
        return ({
          url:`${transferPath}/onafriq/validate-account`,
          method: 'POST',
          body,
        })
      }
    }),
    getOnafriqRate: builder.mutation<GetOnafriqRateResponse, GetOnafriqRateRequest>({
      query: (body) => {
        return ({
          url:`${transferPath}/onafriq/rate`,
          method: 'POST',
          body,
        })
      }
    }),

    // Papps transfers endpoints

    initiatePappsTransfer: builder.mutation<InitiatePappsTransferResponse, InitiatePappsTransferRequest>({
      query: (body) => {
        const { otpCode, emailOrPhoneNumber, otpIdentifier, payload } = body
        return ({
          url:`${transferPath}/initiate-papps-transfer`,
          method: 'post',
          body: payload,
          params: { otpCode, emailOrPhoneNumber, otpIdentifier },
        })
      },
      invalidatesTags: ['transferHistory']
    }),

    fetchPappsBanks: builder.query<FetchPappsBanksResponse, string>({
      query: (countryCode) => `${transferPath}/papps-bank?countryCode=${countryCode}`
    }),

    validatePappsAccountDetails: builder.mutation<any, {
      bank: string,
      bic: string,
      accountNumber: string,
      currency: string,
    }>({
      query: ({bank, bic, accountNumber, currency}) => {
        return ({
          url: `${transferPath}/validate-papss-account`,
          method: 'post',
          body: { bank, bic, accountNumber, currency }
        })
      }
    }),

    FetchPappsTransactionRate: builder.mutation<FetchPappsRateResponse, FetchPappsRatesRequest>({
      query: (body) => {
        return ({
          url: `${transferPath}/papps-rates`,
          method: 'post',
          body,
        })
      }
    }),

    getStandingOrders: builder.query<StandingOrderResponse, GetStandingOrdersRequest>({
      query: ({ accountNumber, companyId, ...params }) => {
        return ({
          url:`${transferPath}/standing-orders`,
          method: 'GET',
          params,
          headers: { accountNumber, companyId: companyId || undefined }
        })
      },
      providesTags: ['scheduleTransferHistory']
    }),
    
    deleteStandingOrder: builder.mutation<DeleteScheduledResponse, DeleteStandingOrderRequest>({
      query: (params) => {
        return ({
          url:`${transferPath}/standing-order`,
          method: 'DELETE',
          // params,
          headers: params
        })
      },
      invalidatesTags: ['scheduleTransferHistory']
    }),

    getAuthorizationsPendingApproval: builder.query<GetAuthorizationPendingApprovalList, { page: number, }>({
      query: ({ page }) => {
        return ({
          url: '/mandate-service/transaction/view-approval',
          method: 'GET',
          params: { page }
        })
      },
      providesTags: ['authorizationsApprovals']
    }),

    reviewPendingApproval: builder.mutation<any, {
      transactionIds: string[],
      comment: string,
      status: string,
    }>({
      query: (body) => {
        return ({
          url: '/mandate-service/transaction/approval',
          method: 'POST',
          body,
        })
      },
      invalidatesTags: ['authorizationsApprovals', 'authorizationsHistory']
    }),

    getAuthorizationsHistory: builder.query<GetAuthorizationPendingApprovalList, { page?: number, }>({
      query: ({ page }) => {
        return ({
          url: '/mandate-service/transaction/view-approval',
          method: 'GET',
          params: { page }
        })
      },
      providesTags: ['authorizationsHistory']
    }),

    getTransactionsHistory: builder.query<TransactionHistoryResponse, { page?: number, limit?: number, billType?: string, }>({
      query: ({ page, limit, billType }) => {
        return ({
          url: 'biller-service/transaction-history',
          method: 'GET',
          params: { page, limit, billType }
        })
      },
      providesTags: ['authorizationsHistory']
    }),

    getTransactionsApprovalHistory: builder.query<any, { transactionId?: string, }>({
      query: ({ transactionId }) => {
        return ({
          url: 'biller-service/pending-approval-history',
          method: 'GET',
          params: { transactionId },
        })
      },
      // providesTags: ['authorizationsHistory']
    }),

    getApprovalProcessHistory: builder.query<GetAuthorizationPendingApprovalList, { page?: number, limit?: number, status?: string,}>({
      query: ({ page, limit, status }) => {
        return ({
          url: 'mandate-service/transaction/view-approval-history',
          method: 'GET',
          params: { page, limit, status },
        })
      },
      providesTags: ['authorizationsHistory']
    }),

    getCategoryAuthorizers: builder.query<any, { category: string, accountNumber: string, }>({
      query: ({ category, accountNumber }) => {
        return ({
          url: 'mandate-service/transaction/category-user',
          method: 'GET',
          params: { category, accountNumber },
        })
      },
      // providesTags: ['authorizationsHistory']
    }),
    validateNegotiatedCode: builder.query<NegotiatedRateResponse, { rateCode: string, }>({
      query: ({ rateCode }) => {
        return ({
          url:`${transferPath}/validate-rate-code`,
          method: 'GET',
          params: { rateCode }
        })
      },
    }),

    getNegotiatedCodes: builder.query<GetNegotiatedRates, { page?: number, limit?: number, debitAccount: string, status?: string, }>({
      query: ({ page, limit, debitAccount, status }) => {
        return ({
          url:`${transferPath}/generated-codes`,
          method: 'GET',
          params: { page, limit, debitAccount, status }
        })
      },
    }),

    getFXRates: builder.query<FXRatesResponse[], { serviceName?: string, }>({
      query: ({ serviceName }) => {
        return ({
          url:`${inquiryPath}//exchange-rate`,
          method: 'GET',
          params: { serviceName },
        })
      },
    }),

    getTransactionHistoryV2: builder.query<TransactionHistoryResponse, { debitAccount: string, page?: number, limit?: number, transactionType?: string, }>({
      query: ({ debitAccount, page, limit, transactionType }) => {
        return ({
          url: `${transferPath}/view-transaction/v2`,
          method: 'GET',
          params: { debitAccount, page, limit, transactionType }
        })
      },
      // providesTags: ['authorizationsHistory']
    }),

    getBkRemitTransferPurpose: builder.query<any, string>({
      query: () => {
        return ({
          url:`${transferPath}/transfer-purposes`,
          method: 'GET',
        })
      }
    }),

    getOneTransferHistory: builder.query<TransferResponse, {transactionId: string,}>({
      query: ({ transactionId }) => {
        return ({
          url:`${transferPath}/view-transfer/${transactionId}/transaction-detail`,
          method: 'GET',
        })
      },
    }),

    fetchArchivedTransfers: builder.query<any, any>({
      query: (params) => {
        return ({
          url:`${transferPath}/view-legacy-transaction`,
          method: 'GET',
          params,
        })
      },
      providesTags: ['transferHistory']
    }),

    // Endpoint to get hidden services
    getServices: builder.query<string[], void>({
      query: () => {
        return ({
          url: `${transferPath}/services`,
          method: 'GET',
        })
      }
    }),
  })

export default transferEndPoint